import { isQuestionVisible } from "./showIf";
import ChoiceButtons from "./ChoiceButtons";

function ScaleSelect({ value, onChange, disabled, specialOptions = [], compact = false }) {
    const selectValue = value === "na" && specialOptions.length
        ? specialOptions[0].value
        : (value ?? "");
    return (
        <select
            className={compact ? "mt-1.5 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus:border-amber-400 disabled:bg-slate-100" : "mt-3 min-h-12 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100 disabled:bg-slate-100"}
            value={selectValue}
            disabled={disabled}
            onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") onChange(undefined);
                else if (/^[1-5]$/.test(raw)) onChange(Number(raw));
                else onChange(raw);
            }}
        >
            <option value="">—</option>
            {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                    {n}
                </option>
            ))}
            {specialOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    );
}

export default function AdaptationQuestionFields({ questions, values, onChange, coreHistory, disabled, compact = false }) {
    const visible = (questions || []).filter((q) => isQuestionVisible(q, values, coreHistory));
    // compact — для предпросмотра в конструкторе (Чат 3, №7): меньше отступов, мельче шрифт.
    const card = compact ? "block rounded-lg border border-slate-200 bg-white p-2.5" : "block rounded-2xl border border-slate-200 bg-slate-50/70 p-5";
    return (
        <div className={compact ? "space-y-1.5" : "space-y-5"}>
            {visible.map((q) => {
                const current = values[q.id];
                const heading = <>
                    <span className={compact ? "text-sm font-semibold leading-5 text-slate-900" : "text-base font-semibold leading-6 text-slate-900"}>
                        {q.text}
                        {q.required === false ? <span className="ml-1 font-normal text-slate-400">(необязательно)</span> : null}
                    </span>
                    {q.hint ? <small className="mt-1 block text-sm leading-5 text-slate-500">{q.hint}</small> : null}
                </>;
                if (q.type === "multi") {
                    return <fieldset key={q.id} className={card} data-testid={`q-${q.id}`} disabled={disabled}>
                        <legend className="w-full px-1">{heading}</legend>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                            {(q.options || []).map((opt) => {
                                const selected = Array.isArray(current) ? current.includes(opt) : false;
                                return <label key={opt} className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition ${selected ? "border-amber-400 bg-amber-50 text-slate-950" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>
                                    <input
                                        className="h-4 w-4 accent-amber-500"
                                        type="checkbox"
                                        checked={selected}
                                        onChange={(e) => {
                                            const prev = Array.isArray(current) ? current : [];
                                            const exclusive = new Set(q.exclusive_options || []);
                                            let next;
                                            if (!e.target.checked) next = prev.filter((x) => x !== opt);
                                            else if (exclusive.has(opt)) next = [opt];
                                            else next = [...prev.filter((x) => !exclusive.has(x)), opt];
                                            onChange(q.id, [...new Set(next)]);
                                        }}
                                    />
                                    {opt}
                                </label>;
                            })}
                        </div>
                    </fieldset>;
                }
                if (q.type === "scale" || q.type === "yes_no") {
                    // [Claude] 2026-11-02 — гибкая шкала и «да/нет» из конструктора адаптации (№7)
                    return <fieldset key={q.id} className={card} data-testid={`q-${q.id}`} disabled={disabled}>
                        <legend className="w-full px-1">{heading}</legend>
                        <ChoiceButtons question={q} value={current} disabled={disabled} compact={compact} onChange={(v) => onChange(q.id, v)} />
                    </fieldset>;
                }
                if (q.type === "number") {
                    return <label key={q.id} className={card} data-testid={`q-${q.id}`}>
                        {heading}
                        <input
                            type="number"
                            className={compact ? "mt-1.5 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus:border-amber-400 disabled:bg-slate-100" : "mt-3 min-h-12 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100 disabled:bg-slate-100"}
                            value={current ?? ""}
                            disabled={disabled}
                            min={q.number?.min ?? undefined}
                            max={q.number?.max ?? undefined}
                            onChange={(e) => onChange(q.id, e.target.value === "" ? undefined : Number(e.target.value))}
                        />
                    </label>;
                }
                return (
                    <label key={q.id} className={card} data-testid={`q-${q.id}`}>
                        {heading}
                        {q.type === "scale_1_5" || q.type === "scale_na" || q.type === "scale_obs" ? (
                            <ScaleSelect
                                value={current}
                                onChange={(v) => onChange(q.id, v)}
                                disabled={disabled}
                                specialOptions={q.special_options || []}
                                compact={compact}
                            />
                        ) : q.type === "choice" ? (
                            <select
                                className={compact ? "mt-1.5 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus:border-amber-400 disabled:bg-slate-100" : "mt-3 min-h-12 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100 disabled:bg-slate-100"}
                                value={current ?? ""}
                                disabled={disabled}
                                onChange={(e) => onChange(q.id, e.target.value)}
                            >
                                <option value="">—</option>
                                {(q.options || []).map((opt) => (
                                    <option key={opt} value={opt}>
                                        {opt}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <textarea
                                className={compact ? "mt-1.5 min-h-14 py-1 w-full rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus:border-amber-400 disabled:bg-slate-100" : "mt-3 min-h-28 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base leading-6 outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100 disabled:bg-slate-100"}
                                rows={4}
                                disabled={disabled}
                                value={current || ""}
                                onChange={(e) => onChange(q.id, e.target.value)}
                            />
                        )}
                    </label>
                );
            })}
        </div>
    );
}
